import { call, put, select } from 'redux-saga/effects';
import {
  FAQProps,
  HelpCenterCategory,
  HelpCenterTopic,
  IHelpCenterCommerce,
  IUserAcess,
  USER_ACESS_TYPE
} from './types';
import {
  loadHelpCenterCategoriesFailure,
  loadHelpCenterCategoriesSuccess,
  loadHelpCenterTopicsFailure,
  loadHelpCenterTopicsSuccess,
  loadHelpCenterSuccess,
  loadHelpCenterFailure,
  loadHelpCenterQuerySuccess,
  loadHelpCenterQueryFailure,
  loadHelpCenterQueryTopicsSuccess,
  loadHelpCenterSugestionFailure,
  loadHelpCenterSugestionSuccess
} from './actions';
import { addToast } from '../toast/actions';
import { EToastTypes } from '../toast/types';
import { HelpCenter } from '../../../services/HelpCenter';
import { ISearchResult } from '@gerdau/hefesto/SearchField/SearchField.types';
import { ApplicationState } from '../..';
import { IUserState } from '../user/types';
import { resolveIsCommerceUser } from '../../../utils/commerceUser';
import { getFlagValue } from '../../../utils/getFlagValue';

interface ILoadHelpCenterQuery {
  payload: {
    query: string;
  };
}

interface ILoadHelpCenterTopics {
  payload: {
    categoryId: number;
  };
}

const selectHelpCenter = (state: ApplicationState) => state.helpCenter.faq;
const selectHelpCenterCommerce = (state: ApplicationState) =>
  state.helpCenter.commerce;
const user = (state: ApplicationState) => state.user;
const selectCustomers = (state: ApplicationState) => state.customers.customers;

function* selectKeywordsNotAllowed() {
  const userState: IUserState = yield select(user);
  const customers: ReturnType<typeof selectCustomers> = yield select(
    selectCustomers
  );

  const corteDobra = userState.userPermissions.hasFabricationAccess;
  const novoFluxoCompra = resolveIsCommerceUser(
    userState.journeyViews,
    customers,
    getFlagValue('purchase-migration-brdi-brin')
  );

  const keywordsNotAllowed: IUserAcess[] = [
    ...(corteDobra ? [] : [USER_ACESS_TYPE.CORTE_E_DOBRA]),
    ...(novoFluxoCompra ? [] : [USER_ACESS_TYPE.COMMERCE_MAGENTO])
  ];

  return keywordsNotAllowed;
}

const parseKeywords = (raw: string) =>
  (raw ?? '')
    .split(';')
    .map((keyword) => keyword.trim())
    .filter(Boolean);

const filterAllowedTopics = (
  topics: HelpCenterTopic[],
  keywordsNotAllowed: IUserAcess[]
) =>
  topics.filter(
    (topic) =>
      !parseKeywords(topic.meta_keywords).some((keyword) =>
        (keywordsNotAllowed as string[]).includes(keyword)
      )
  );

export function* loadHelpCenterSaga() {
  try {
    const cachedHelpCenter: FAQProps | null = yield select(selectHelpCenter);
    if (
      cachedHelpCenter &&
      cachedHelpCenter.categories?.length > 0 &&
      cachedHelpCenter.question?.length > 0
    ) {
      yield put(loadHelpCenterSuccess(cachedHelpCenter));
      return;
    }

    const keywordsNotAllowed: IUserAcess[] = yield call(
      selectKeywordsNotAllowed
    );

    const helpCenterService = new HelpCenter();
    const helpCenterFn: FAQProps = yield call(
      helpCenterService.getContent.bind(helpCenterService),
      keywordsNotAllowed
    );
    yield put(loadHelpCenterSuccess(helpCenterFn));
  } catch (err) {
    console.error('loadHelpCenterCategory', err);
    yield put(
      addToast({
        message: 'Falha ao buscar manuais ou perguntas.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    yield put(loadHelpCenterFailure(err));
  }
}

export function* loadHelpCenterQuerySaga(params: {
  payload: { faq: FAQProps };
}) {
  const { faq } = params.payload;

  const keywordsNotAllowed: IUserAcess[] = yield call(selectKeywordsNotAllowed);

  try {
    const helpCenterService = new HelpCenter();

    if (getFlagValue('help_center_new_api')) {
      const topics: HelpCenterTopic[] = yield call(
        helpCenterService.searchTopics.bind(helpCenterService),
        faq.query.searchTerm
      );
      yield put(
        loadHelpCenterQueryTopicsSuccess(
          filterAllowedTopics(topics, keywordsNotAllowed)
        )
      );
      return;
    }

    const helpCenterFn: FAQProps = yield call(
      helpCenterService.getContentByQuery.bind(helpCenterService),
      { faq, keywordsNotAllowed }
    );
    yield put(loadHelpCenterQuerySuccess(helpCenterFn));
  } catch (err) {
    console.error('loadHelpCenterCategory', err);
    yield put(
      addToast({
        message: 'Falha ao buscar manuais ou perguntas.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    yield put(loadHelpCenterQueryFailure(err));
  }
}

export function* loadHelpCenterSugestionSaga(params: ILoadHelpCenterQuery) {
  const { query } = params.payload;
  try {
    const helpCenterService = new HelpCenter();
    const helpCenterFn: ISearchResult[] = yield call(
      helpCenterService.getContentSugestion.bind(helpCenterService),
      { query }
    );
    yield put(loadHelpCenterSugestionSuccess(helpCenterFn));
  } catch (err) {
    console.error('loadHelpCenterCategory', err);
    yield put(
      addToast({
        message: 'Falha ao buscar manuais ou perguntas.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    yield put(loadHelpCenterSugestionFailure(err));
  }
}

export function* loadHelpCenterCategoriesSaga() {
  try {
    const cached: IHelpCenterCommerce = yield select(selectHelpCenterCommerce);
    if (cached.categories.length > 0) {
      yield put(loadHelpCenterCategoriesSuccess(cached.categories));
      return;
    }

    const helpCenterService = new HelpCenter();
    const categories: HelpCenterCategory[] = yield call(
      helpCenterService.getCategories.bind(helpCenterService)
    );
    yield put(loadHelpCenterCategoriesSuccess(categories));
  } catch (err) {
    console.error('loadHelpCenterCategories', err);
    yield put(
      addToast({
        message: 'Falha ao buscar manuais ou perguntas.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    yield put(loadHelpCenterCategoriesFailure(err));
  }
}

export function* loadHelpCenterTopicsSaga(params: ILoadHelpCenterTopics) {
  const { categoryId } = params.payload;
  try {
    const cached: IHelpCenterCommerce = yield select(selectHelpCenterCommerce);
    const cachedTopics = cached.topicsByCategory[categoryId];
    if (cachedTopics) {
      yield put(loadHelpCenterTopicsSuccess(categoryId, cachedTopics));
      return;
    }

    const keywordsNotAllowed: IUserAcess[] = yield call(
      selectKeywordsNotAllowed
    );

    const helpCenterService = new HelpCenter();
    const topics: HelpCenterTopic[] = yield call(
      helpCenterService.getTopics.bind(helpCenterService),
      categoryId
    );

    const allowedTopics = filterAllowedTopics(topics, keywordsNotAllowed);

    yield put(loadHelpCenterTopicsSuccess(categoryId, allowedTopics));
  } catch (err) {
    console.error('loadHelpCenterTopics', err);
    yield put(
      addToast({
        message: 'Falha ao buscar manuais ou perguntas.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    yield put(loadHelpCenterTopicsFailure(err));
  }
}
